/**
 * The bullet rows (PRD 10.3, V-3; Phase 24): the selected week of the heatmap read as hours
 * instead of colour — one row per entity with the threshold bands as its background, the
 * allocated hours as a bar, the high-confidence hours as the inner bar, and capacity as a tick.
 *
 * The heatmap answers "which week hurts"; the bullet rows answer "by how many hours, and how
 * much of it is committed". They are the same numbers: every figure here is a field of the
 * `GET /forecast/heatmap` cell for that row and week, so a percentage on a bullet row and the
 * cell above it cannot disagree (Phase 24's gate is exactly that comparison).
 *
 * Shared by the browser client and the tests, as `heatmap.js` is, so the view and the checks
 * read the same module. Pure: no DOM, no I/O.
 */
import { STATUS_STATES } from './palette.js';
import { col } from './table.js';
import { ROW_NOUNS } from './heatmap.js';
import { round2, thresholdScale } from './heatmap.js';
import { ticks } from './scales.js';

/** The orders the card offers. `allocation` is the default: the row that hurts most, first. */
export const BULLET_SORTS = Object.freeze(['allocation', 'over', 'label']);

/**
 * How far past the largest figure the shared axis runs, so a bar never ends flush against the
 * frame and the overloaded band always has room to show.
 */
export const SCALE_HEADROOM = 1.08;

const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
const hours = (v) => round2(num(v) ?? 0);
const pct = (part, whole) => (whole > 0 ? Math.min(100, round2((part / whole) * 100)) : 0);

/** The weeks this response can draw bullet rows for, in the API's order. */
export function bulletWeeks(data) {
  return (data?.totals ?? []).map((t) => ({
    week_start: t.week_start,
    week_end: t.week_end,
    allocated_hours: hours(t.allocated_hours),
    capacity_hours: hours(t.capacity_hours),
    utilization: num(t.utilization),
    band: t.band ?? null,
  }));
}

/** The company's worst week in the window (ties to the earlier one); null when none has capacity. */
export function peakWeek(data) {
  let best = null;
  for (const w of bulletWeeks(data)) {
    if (w.utilization === null) continue;
    if (best === null || w.utilization > best.utilization) best = w;
  }
  return best?.week_start ?? null;
}

/**
 * The week the rows are drawn for: the one asked for when the window still holds it, else the
 * company's peak week, else the first week. Null when the response has no weeks at all.
 * @param {object} data  a `GET /forecast/heatmap` response
 * @param {string | null} [requested]
 */
export function resolveWeek(data, requested = null) {
  const weeks = bulletWeeks(data);
  if (!weeks.length) return null;
  if (requested && weeks.some((w) => w.week_start === requested)) return requested;
  return peakWeek(data) ?? weeks[0].week_start;
}

/** One drawn row from a heatmap cell: the cell's own fields, plus what the labels state. */
function rowFrom(source, cell) {
  const allocated = hours(cell.allocated_hours);
  const capacity = hours(cell.capacity_hours);
  const high = hours(cell.high_confidence_hours);
  const utilization = num(cell.utilization);
  return {
    key: source.key,
    label: source.label,
    entity: source.entity ?? null,
    cell,
    week_start: cell.week_start,
    allocated_hours: allocated,
    high_confidence_hours: high,
    // What is not high confidence: inferred, analog-derived and pipeline work, drawn hatched.
    other_hours: round2(Math.max(0, allocated - high)),
    capacity_hours: capacity,
    utilization,
    band: cell.band ?? null,
    has_capacity: capacity > 0,
    // Hours over are hours over *somebody's* capacity. A row with nobody staffed to it is not
    // overloaded, it is unstaffed: those hours are counted on their own so the two never mix.
    over_hours: capacity > 0 ? round2(Math.max(0, allocated - capacity)) : 0,
    headroom_hours: capacity > 0 ? round2(Math.max(0, capacity - allocated)) : 0,
    unstaffed_hours: capacity > 0 ? 0 : allocated,
  };
}

/**
 * The role-only hours of a week as a row: demand nobody is named for, so hours and no ratio.
 * Only the individual dimension has a line for them — in every other dimension the hours are
 * already inside the rows (a role-only row still has a service), and a line would double count.
 * The layer split of those hours is not a field of the response, so the row carries only what
 * the API stated — the hours — and the table prints dashes rather than the company's split.
 */
function unassignedRow(dimension, total) {
  if (dimension !== 'individual') return null;
  const h = hours(total?.unassigned_hours);
  if (!(h > 0)) return null;
  return rowFrom(
    { key: 'unassigned', label: 'Role-only (nobody named)', entity: null },
    {
      week_start: total.week_start,
      week_end: total.week_end,
      allocated_hours: h,
      high_confidence_hours: 0,
      capacity_hours: 0,
      utilization: null,
      band: null,
      explicit_hours: null,
      inferred_hours: null,
      recurring_hours: null,
      nonproject_hours: null,
      pipeline_hours: null,
      people: 0,
      projects: null,
      opportunities: null,
    },
  );
}

/**
 * Order the rows. `allocation` and `over` descend, rows without capacity last (their percentage
 * does not exist and their hours are a different question), ties by the API's order.
 * @param {object[]} rows
 * @param {'allocation' | 'over' | 'label'} by
 */
export function sortBulletRows(rows, by = 'allocation') {
  const list = [...(rows ?? [])];
  if (by === 'label') return list;
  const measure = (r) => (by === 'over' ? r.over_hours : r.utilization);
  return list
    .map((r, i) => ({ r, i, m: r.has_capacity ? measure(r) : null }))
    .sort((a, b) => {
      if (a.m === null && b.m === null) return a.i - b.i;
      if (a.m === null) return 1;
      if (b.m === null) return -1;
      return b.m - a.m || a.i - b.i;
    })
    .map((x) => x.r);
}

/**
 * Everything a bullet row card draws for one week of a heatmap response: the rows, the company
 * total, the role-only line, and the shared hours axis they are measured on.
 *
 * The axis is shared across the rows deliberately (PRD 10.3 draws three rows of different
 * lengths against one scale): a row's bar is comparable with every other row's, and the tick
 * moves because capacity differs, not because the scale does. It is measured on the entity rows
 * alone. The company total and the role-only line are sums of those rows, not peers of them —
 * on the dev database the company is 4,087 h in a week where the largest person is 110 h — so
 * they are drawn as figures beneath the chart rather than as bars that would flatten every row
 * above them into a sliver.
 *
 * @param {object} data  a `GET /forecast/heatmap` response
 * @param {{ week?: string | null, sort?: 'allocation' | 'over' | 'label' }} [opts]
 */
export function bulletModel(data, { week = null, sort = 'allocation' } = {}) {
  const weeks = bulletWeeks(data);
  const weekStart = resolveWeek(data, week);
  if (!weekStart) return null;
  const index = weeks.findIndex((w) => w.week_start === weekStart);
  const totalCell = (data.totals ?? []).find((t) => t.week_start === weekStart);
  const rows = [];
  for (const source of data.rows ?? []) {
    const cell = (source.cells ?? []).find((c) => c.week_start === weekStart);
    if (cell) rows.push(rowFrom(source, cell));
  }
  const sorted = sortBulletRows(rows, sort);
  const total = rowFrom({ key: 'company', label: 'Company', entity: null }, totalCell);
  const unassigned = unassignedRow(data.dimension, totalCell);
  // The axis is measured on the rows that have capacity: those are the ones a bar is read
  // against a tick on. A row with nobody staffed to it has no ratio, and on the dev database
  // one of them (the department nobody is named for) carries 3,342 h in a week where the
  // largest real row is 186 h — so it is clipped with a marker rather than allowed to flatten
  // every row that does have a tick. With nothing staffed at all, the rows set the scale
  // themselves, and with no rows at all, the company does.
  const staffed = sorted.filter((r) => r.has_capacity);
  const measured = staffed.length ? staffed : sorted.length ? sorted : [total];
  const largest = Math.max(...measured.flatMap((r) => [r.allocated_hours, r.capacity_hours]), 0);
  const scale = ticks([0, largest * SCALE_HEADROOM], { count: 4 });
  return {
    week: {
      week_start: weekStart,
      week_end: weeks[index].week_end,
      index,
      count: weeks.length,
      is_peak: weekStart === peakWeek(data),
    },
    weeks,
    rows: sorted,
    total,
    unassigned,
    // What the foot states in figures: the role-only hours, then the company, in that order.
    foot: [
      ...(unassigned ? [unassigned] : []),
      // In the company dimension the one row already is the company; a foot would repeat it.
      ...(sorted.length && data.dimension !== 'company' ? [total] : []),
    ],
    sort,
    dimension: data.dimension,
    scale: { max: scale.domain[1], step: scale.step, values: scale.values },
  };
}

/**
 * Where a row's marks sit on the shared axis, as percentages of it: the threshold bands that
 * back the track, the allocated bar, the high-confidence inner bar, and the capacity tick.
 *
 * The bands are the run's own thresholds applied to *this row's* capacity (a bullet chart's
 * qualitative ranges), which is why a bar landing in the caution band means the same thing on
 * every row however different their hours are. A row with no capacity has no bands and no tick.
 *
 * @param {ReturnType<typeof bulletModel>['rows'][number]} row
 * @param {number} max  the axis maximum from the model's scale
 * @param {object} thresholds  the run's thresholds
 */
export function bulletGeometry(row, max, thresholds) {
  const scale = thresholdScale(thresholds);
  const bands = [];
  if (row.has_capacity) {
    let from = 0;
    for (const state of scale) {
      const to = state.max === null ? max : Math.min(max, row.capacity_hours * state.max);
      if (to > from) bands.push({ key: state.key, from: pct(from, max), to: pct(to, max) });
      from = to;
      if (from >= max) break;
    }
  }
  return {
    bands,
    bar: pct(row.allocated_hours, max),
    inner: pct(row.high_confidence_hours, max),
    tick: row.has_capacity ? pct(row.capacity_hours, max) : null,
    // A bar longer than the axis is drawn to the end and marked, never quietly truncated; the
    // hours beside it are the figure, and only an unstaffed row can normally get here.
    clipped: max > 0 && row.allocated_hours > max,
  };
}

/** The figures the card's header states for the selected week. */
export function bulletSummary(model) {
  const perBand = Object.fromEntries(STATUS_STATES.map((s) => [s.key, 0]));
  let over = 0;
  let overHours = 0;
  let noCapacity = 0;
  let unstaffedHours = 0;
  for (const row of model?.rows ?? []) {
    if (row.band) perBand[row.band] += 1;
    else {
      noCapacity += 1;
      unstaffedHours += row.unstaffed_hours;
    }
    if (row.over_hours > 0) {
      over += 1;
      overHours += row.over_hours;
    }
  }
  return {
    rows: model?.rows?.length ?? 0,
    per_band: perBand,
    no_capacity: noCapacity,
    unstaffed_hours: round2(unstaffedHours),
    rows_over: over,
    hours_over: round2(overHours),
  };
}

/** The table view's rows: one per drawn row, with the same numbers as the bars. */
export function bulletExportRows(model) {
  const all = [...(model?.rows ?? [])];
  if (model?.unassigned) all.push(model.unassigned);
  if (model?.total) all.push(model.total);
  return all.map((r) => ({
    row: r.label,
    key: r.key,
    week_start: r.week_start,
    allocated_hours: r.allocated_hours,
    high_confidence_hours: r.high_confidence_hours,
    other_hours: r.other_hours,
    capacity_hours: r.capacity_hours,
    over_hours: r.over_hours,
    headroom_hours: r.headroom_hours,
    unstaffed_hours: r.unstaffed_hours,
    utilization: r.utilization,
    band: r.band,
    explicit_hours: r.cell.explicit_hours,
    inferred_hours: r.cell.inferred_hours,
    recurring_hours: r.cell.recurring_hours,
    nonproject_hours: r.cell.nonproject_hours,
    pipeline_hours: r.cell.pipeline_hours,
    people: r.cell.people,
    projects: r.cell.projects,
    opportunities: r.cell.opportunities,
  }));
}

// ---------------------------------------------------------------------------------------------
// Table columns (Phase 37)
//
// A specification, not a rendering: the screen decorates it with `withFormats` and the CSV
// writer reads the same list, so an export cannot hold a different set of columns from the
// table it came from.

/** @param {{ dimension?: string }} opts */
export function bulletColumns({ dimension = 'company' } = {}) {
  return [
    col.text('row', (ROW_NOUNS[dimension] ?? ROW_NOUNS.company)[0]),
    col.hours('allocated_hours', 'Allocated'),
    col.hours('high_confidence_hours', 'High confidence'),
    col.hours('other_hours', 'Rest'),
    col.hours('capacity_hours', 'Capacity'),
    col.hours('over_hours', 'Over'),
    col.hours('headroom_hours', 'Headroom'),
    col.percent('utilization', 'Allocation'),
    col.text('band', 'State', { csv: (v) => v ?? '' }),
    col.hours('explicit_hours', 'Explicit'),
    col.hours('inferred_hours', 'Inferred'),
    col.hours('recurring_hours', 'Recurring'),
    col.hours('nonproject_hours', 'Non-project'),
    col.hours('pipeline_hours', 'Pipeline'),
    col.count('people', 'People'),
    col.count('projects', 'Projects'),
    col.count('opportunities', 'Deals'),
  ];
}
